import React, { useMemo, useState } from "react";

import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { createNewUser } from "libLegacy/api/dashboard";

import { PageFrame } from "components/auth/PageFrame";
import { LOGIN } from "routers/routePaths";

const nameValidator = /(\b[a-z][^\d\s]+\b)/gi;
const emailValidator = /(\b\S+\b)[@]?(\b\w+\b)[.](\b\w+\b)/gi;

export const CreateAccount = ({ history }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [, setError] = useState<string>("");

  const isNameValid = useMemo(() => {
    const matches = name.match(nameValidator);
    return !name || (name && matches && matches.length > 1);
  }, [name]);

  const isEmailValid = useMemo(() => {
    return !email || email.match(emailValidator);
  }, [email]);

  const isPasswordValid = useMemo(() => {
    return !password || (password && password.length > 7);
  }, [password]);

  const isConfirmPasswordValid = useMemo(() => {
    return (
      !(password && confirmPassword) ||
      (password &&
        isPasswordValid &&
        confirmPassword &&
        confirmPassword === password)
    );
  }, [confirmPassword, password, isPasswordValid]);

  const isFormValid = useMemo(
    () =>
      name &&
      isNameValid &&
      email &&
      isEmailValid &&
      password &&
      isPasswordValid &&
      confirmPassword &&
      isConfirmPasswordValid,
    [
      name,
      isNameValid,
      email,
      isEmailValid,
      password,
      isPasswordValid,
      confirmPassword,
      isConfirmPasswordValid,
    ]
  );

  const handleClick = async () => {
    setIsLoading(true);
    try {
      await createNewUser({ name, email, password });
      history.push(LOGIN);
    } catch (e) {
      setError("Falha ao criar usuário");
      setIsLoading(false);
    }
  };

  const toggleVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  const passwordIcon = (
    <InputAdornment position="end">
      <IconButton onClick={toggleVisibility}>
        {isPasswordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <PageFrame
      title="Todas as soluções de energia em uma única plataforma."
      centerText
      buttonData={{
        text: isLoading ? (
          <CircularProgress size={24} color="secondary" />
        ) : (
          "Criar conta"
        ),
        onClick: handleClick,
        disabled: !isFormValid || isLoading,
      }}
    >
      <TextField
        id="name"
        label="Seu come completo"
        variant="outlined"
        value={name}
        error={!isNameValid}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        sx={{ mb: 1 }}
      />

      <TextField
        id="email"
        label="Seu e-mail"
        variant="outlined"
        value={email}
        error={!isEmailValid}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        sx={{ mt: 2, mb: 1 }}
      />

      <TextField
        id="password"
        label="Crie sua senha"
        variant="outlined"
        color="primary"
        fullWidth
        type={isPasswordVisible ? "text" : "password"}
        InputProps={{ endAdornment: passwordIcon }}
        value={password}
        error={!isPasswordValid}
        onChange={(e) => setPassword(e.target.value)}
        sx={{ mt: 2, mb: 1 }}
      />

      <TextField
        id="passwordConfirm"
        label="Confirme sua senha"
        variant="outlined"
        color="primary"
        fullWidth
        type={isPasswordVisible ? "text" : "password"}
        InputProps={{ endAdornment: passwordIcon }}
        value={confirmPassword}
        error={!isConfirmPasswordValid}
        onChange={(e) => setConfirmPassword(e.target.value)}
        sx={{ mt: 2 }}
      />
    </PageFrame>
  );
};
